import { useTranslation } from "react-i18next";
import type { CenterCourse } from "../../../center";

interface MentoringModuleFieldsProps {
    courses: CenterCourse[];
    courseId: string;
    moduleId: string;
    onCourseChange: (courseId: string) => void;
    onModuleChange: (moduleId: string) => void;
    disabled?: boolean;
}

const SELECT_CLASS =
    "w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-900 disabled:opacity-60";

/**
 * The course and module a training centre's mentoring works on, chosen while
 * booking it. Both optional: a module can also be given later, from the
 * session.
 */
export default function MentoringModuleFields({
    courses,
    courseId,
    moduleId,
    onCourseChange,
    onModuleChange,
    disabled = false,
}: MentoringModuleFieldsProps) {
    const { t } = useTranslation();
    const course = courses.find((option) => option.id === courseId) ?? null;
    // A module not saved yet has no id, and nothing to point a mentoring at.
    const modules = (course?.phases ?? []).filter(
        (phase): phase is typeof phase & { id: string } => Boolean(phase.id)
    );

    return (
        <div className="flex flex-col gap-3 rounded-lg border border-gray-200 p-3 dark:border-gray-700">
            <div className="grid gap-3 sm:grid-cols-2">
                <label className="flex min-w-0 flex-col gap-1 text-sm">
                    {t("scheduler.schedule.course")}
                    <select
                        aria-label={t("scheduler.schedule.course")}
                        value={courseId}
                        onChange={(event) => onCourseChange(event.target.value)}
                        disabled={disabled}
                        className={SELECT_CLASS}
                    >
                        <option value="">{t("scheduler.schedule.noCourse")}</option>
                        {courses.map((option) => (
                            <option key={option.id} value={option.id}>
                                {option.internal_name && option.internal_name !== option.name
                                    ? `${option.name} · ${option.internal_name}`
                                    : option.name}
                            </option>
                        ))}
                    </select>
                </label>

                <label className="flex min-w-0 flex-col gap-1 text-sm">
                    {t("scheduler.schedule.module")}
                    <select
                        aria-label={t("scheduler.schedule.module")}
                        value={moduleId}
                        onChange={(event) => onModuleChange(event.target.value)}
                        disabled={disabled || modules.length === 0}
                        className={SELECT_CLASS}
                    >
                        <option value="">{t("scheduler.schedule.noModule")}</option>
                        {modules.map((phase) => (
                            <option key={phase.id} value={phase.id}>
                                {phase.name}
                            </option>
                        ))}
                    </select>
                </label>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
                {course && modules.length === 0
                    ? t("scheduler.schedule.noModules")
                    : t("scheduler.schedule.courseModuleHint")}
            </p>
        </div>
    );
}
